import { MDBCardBody, MDBCardHeader } from "mdb-react-ui-kit"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import { useRecoilState } from "recoil"
import {
    ConfirmationModal,
    FormAutocomplete,
    Input,
    NubImageUpload,
    RichEditor,
    RowInfo,
} from "../common"
import { Button, Row, Col, DesignationItemActionButtonWrapper } from "../App.styles"
import { highlightIdState, useDesignationItemsQuery, useQueryInvalidateInfinite, useQueryNoInvalidate } from "./Designation.state"
import DescriptionModal from "./modals/DescriptionModal"
import TitleModal from "./modals/TitleModal"
import ImageModal from "./modals/ImageModal"
import DesignationTypeModal from './modals/DesignationTypeModal'
import DesignationValueModal from './modals/DesignationValueModal'

const DesignationItemEditWithMedia = ({ id, onClose, onDelete: destroy, isDeleting }) => {
    const { t } = useTranslation()
    const useQuery = (config) => useDesignationItemsQuery(id, config)
    const useQueryNoInvalidate_ = () => useQueryNoInvalidate(id)
    const useQueryInvalidateInfinite_ = () => useQueryInvalidateInfinite(id)
    const [, setHighlightId] = useRecoilState(highlightIdState)
    const onSave = async () => {
        await onClose()
        setHighlightId(id)
    }

    const ActionDelete = () => (
        <ConfirmationModal
            title={t("Confirm Deletion")}
            body={t("Are you sure you wish to delete?")}
            confirmLabel={t("Delete")}
            confirmAction={() => {
                onClose && onClose()
                destroy(id)
            }}
            isLoading={isDeleting}
        >
            <Button disabled={isDeleting} className="btn--flat" color="warning">
                <FontAwesomeIcon icon={faTrash} /> {t('Delete')}
            </Button>
        </ConfirmationModal>
    )

    return (
        <>
            <MDBCardHeader>
                <Row>
                    <Col size="12" lg="auto" className="align-items-start mb-4 col--stretch col--grow">
                        <RowInfo help={<TitleModal />}>
                            <Input
                                data-testid="designation-item__input-title"
                                name="name"
                                label={t("Name")}
                                useQuery={useQueryInvalidateInfinite_}
                                mode="clean"
                                maxLength={250}
                            />
                        </RowInfo>
                    </Col>
                </Row>
            </MDBCardHeader>
            <MDBCardBody>
                <Row>
                    <Col size="12" lg="auto" className="align-items-start mb-4 col--stretch col--grow">
                        <RowInfo help={<ImageModal />} isFull>
                            <NubImageUpload storageMode="legacy" name="media" label={t("Image")} useQuery={useQuery} standalone withAi />
                        </RowInfo>
                    </Col>
                </Row>
                <div className="mt-4 mt-lg-0 mb-4">
                    <RowInfo help={<DescriptionModal />}>
                        <RichEditor
                            label={t("Description")}
                            name="description"
                            useQuery={useQueryNoInvalidate_}
                            charCounterMax={250}
                        />
                    </RowInfo>
                </div>
                <div className="mt-4 mt-lg-0 mb-4">
                    <RowInfo help={<DesignationTypeModal />}>
                        <FormAutocomplete
                            options={[{
                                id: 'points',
                                name: t(`Points`),
                            }, {
                                id: 'tasks',
                                name: t(`Challenges`)
                            }]}
                            label={t(`Type`)}
                            name="trigger_type"
                            useQuery={useQueryInvalidateInfinite_}
                            size="small"
                        />
                    </RowInfo>
                </div>
                <div className="mt-4 mt-lg-0 mb-4">
                    <RowInfo help={<DesignationValueModal />}>
                        <FormAutocomplete
                            options={[
                                ...Array(999)
                                    .fill()
                                    .map((_, i) => ({
                                        id: i + 1,
                                        name: String(i + 1),
                                    }))
                            ]}
                            label={t(`Number Required`)}
                            name="trigger_value"
                            useQuery={useQueryInvalidateInfinite_}
                            size="small"
                            creatable
                        />
                    </RowInfo>
                </div>
                <DesignationItemActionButtonWrapper className="justify-content-between d-flex mb-md-2 g-2">
                    <Col size="6" lg="4" className="col--stretch">
                        <ActionDelete />
                    </Col>
                    <Col size="6" lg="4" className="col--stretch justify-content-end">
                        <Button data-testid="designation-item__button-save" color="primary" className="btn--flat" onClick={onSave}>
                            {t('Save')}
                        </Button>
                    </Col>
                </DesignationItemActionButtonWrapper>
            </MDBCardBody>
        </>
    )
}

export default DesignationItemEditWithMedia
